// src/data/content.ts
// Central source of truth for EF21's organisational copy so that the same
// wording is reused across the home, about, what-we-do and goals pages.

export const ORG_NAME = 'Empower Future 21';
export const ORG_SHORT_NAME = 'EF21';

export const TAGLINE = 'Locally Led. Community-Centred. Evidence-Driven.';

export const HEADLINE =
  'Strengthening Education and Resilient Communities in Crisis-Affected Myanmar';

export const INTRO_PARAGRAPHS = [
  'Empower Future 21 (EF21) is a women-led Myanmar humanitarian and development organization working alongside conflict-affected communities to improve access to safe, inclusive, and quality education while strengthening community resilience.',
  'Through locally led action, evidence-informed programming, and strategic partnerships, we support children, adolescents, educators, and communities to recover, learn, and build a more resilient future.',
];

export const WHO_WE_ARE = [
  'Empower Future 21 (EF21) is an independent, women-led Myanmar organization dedicated to advancing equitable access to education and strengthening community resilience in crisis-affected settings.',
  'We work with children, adolescents, volunteer teachers, community leaders, and local organizations to design and implement programmes that respond to locally identified priorities. Our work combines education programming, community engagement, research, and organizational learning to strengthen humanitarian and development responses across Myanmar.',
  'As a growing national organization, we are committed to continuously strengthening our technical expertise, organizational systems, safeguarding practices, and accountability mechanisms to deliver high-quality programmes aligned with international humanitarian standards.',
];

export const VISION =
  'A resilient and inclusive Myanmar where every child, young person, and community has equitable access to quality education, opportunities to thrive, and the capacity to shape their own future.';

export const MISSION =
  'To strengthen education and resilient communities through locally led, evidence-informed, and inclusive humanitarian and development programmes that empower children, youth, educators, and communities affected by crisis.';

export interface Goal {
  number: number;
  title: string;
  description: string;
}

export const GOALS: Goal[] = [
  {
    number: 1,
    title: 'Improve Access to Quality Education',
    description:
      'Expand safe, inclusive, and equitable learning opportunities for children and adolescents affected by conflict, displacement, and crisis.',
  },
  {
    number: 2,
    title: 'Strengthen Educators and Local Capacity',
    description:
      'Build the knowledge, skills, and resilience of teachers, community educators, and local actors to deliver quality education and respond effectively to emergencies.',
  },
  {
    number: 3,
    title: 'Generate Evidence for Better Programming',
    description:
      'Conduct research, assessments, and learning initiatives that inform humanitarian and development responses and strengthen decision-making.',
  },
  {
    number: 4,
    title: 'Strengthen Community Resilience',
    description:
      'Support communities to identify priorities, lead local solutions, and enhance their capacity to prepare for, respond to, and recover from crises.',
  },
  {
    number: 5,
    title: 'Strengthen Organizational Excellence',
    description:
      "Continuously improve EF21's governance, technical capacity, safeguarding, accountability, and institutional systems to deliver high-quality programmes and sustainable impact.",
  },
];

export interface WorkArea {
  id: string;
  icon: string;
  title: string;
  description: string;
  items?: string[];
}

export const WORK_AREAS: WorkArea[] = [
  {
    id: 'education-in-emergencies',
    icon: '📚',
    title: 'Education in Emergencies',
    description:
      'Supporting safe, inclusive, and continuous learning opportunities for children and adolescents affected by conflict, displacement, and crisis.',
    items: [
      'Teacher professional development',
      'Community-based education',
      'Learning continuity',
      'Education needs assessments',
      'Inclusive learning environments',
    ],
  },
  {
    id: 'teacher-capacity-development',
    icon: '👩🏽‍🏫',
    title: 'Teacher Capacity Development',
    description:
      'Strengthening the knowledge, skills, and wellbeing of volunteer teachers and community educators through practical training, mentoring, coaching, and peer learning.',
  },
  {
    id: 'research-learning-evidence',
    icon: '📊',
    title: 'Research, Learning and Evidence',
    description:
      'Generating evidence that informs humanitarian decision-making and improves programme quality.',
    items: [
      'Rapid assessments',
      'Baseline studies',
      'Community consultations',
      'Monitoring, Evaluation, Accountability and Learning (MEAL)',
      'Applied research',
      'Lessons learned and adaptive programming',
    ],
  },
  {
    id: 'community-engagement',
    icon: '🤝',
    title: 'Community Engagement and Local Leadership',
    description:
      'Working alongside communities to identify priorities, strengthen participation, and support locally led solutions that reflect community knowledge and ownership.',
  },
  {
    id: 'mhpss',
    icon: '💙',
    title: 'Mental Health and Psychosocial Support (MHPSS)',
    description:
      'Promoting psychosocial wellbeing by integrating MHPSS approaches into education, community engagement, and teacher support initiatives.',
  },
];

export interface ApproachItem {
  icon: string;
  title: string;
  description: string;
}

export const APPROACH: ApproachItem[] = [
  {
    icon: '🌏',
    title: 'Community-Led',
    description:
      'Communities are active partners throughout programme design, implementation, monitoring, and learning, ensuring that interventions reflect locally identified priorities.',
  },
  {
    icon: '📈',
    title: 'Evidence-Informed',
    description:
      'Our programmes are guided by assessments, research, monitoring data, and continuous learning to ensure that decisions are responsive, adaptive, and accountable.',
  },
  {
    icon: '🤝',
    title: 'Partnership-Driven',
    description:
      'We collaborate with local organizations, civil society networks, technical specialists, humanitarian actors, and donors to maximize collective impact and strengthen locally led responses.',
  },
  {
    icon: '⚖️',
    title: 'Inclusive and Accountable',
    description:
      'We are committed to promoting inclusion, safeguarding, gender equality, protection, transparency, and accountability in all aspects of our work.',
  },
  {
    icon: '🌱',
    title: 'Learning and Systems Strengthening',
    description:
      'As an emerging national organization, we continuously invest in strengthening our people, systems, governance, and institutional capacity to improve programme quality and long-term sustainability.',
  },
];

export interface Reason {
  icon: string;
  title: string;
  description: string;
}

export const WHY_EF21: Reason[] = [
  {
    icon: '📍',
    title: 'Local Knowledge',
    description: 'Deep understanding of communities affected by conflict across Myanmar.',
  },
  {
    icon: '💬',
    title: 'Trusted Community Relationships',
    description: 'Working through local networks and community-based partnerships.',
  },
  {
    icon: '🔍',
    title: 'Evidence-Driven Programming',
    description: 'Using research and community feedback to inform decisions.',
  },
  {
    icon: '✅',
    title: 'Commitment to Quality',
    description:
      'Strengthening organizational systems to meet international humanitarian standards.',
  },
  {
    icon: '🌐',
    title: 'Collaborative Partnerships',
    description:
      'Working with national and international partners to achieve sustainable impact.',
  },
];

export interface ImpactStat {
  icon: string;
  value: string;
  description: string;
}

export const IMPACT_STATS: ImpactStat[] = [
  {
    icon: '📚',
    value: '1,553',
    description:
      'students received Emergency Learning Package (ELP) kits across 11 community-based schools in 6 townships of Sagaing Region.',
  },
  {
    icon: '🚨',
    value: '7',
    description:
      'conflict-affected villages in Wetlet and Mingin Townships supported with rapid emergency humanitarian assistance.',
  },
  {
    icon: '📖',
    value: '2',
    description:
      'community libraries established to improve access to learning resources in underserved communities.',
  },
  {
    icon: '👩🏽‍🏫',
    value: '6',
    description:
      'community teachers trained as volunteer librarians to strengthen library management and reading promotion.',
  },
  {
    icon: '📊',
    value: '70+',
    description:
      'educators participated in rapid assessments to inform evidence-based education programming.',
  },
  {
    icon: '🤝',
    value: '25',
    description:
      'communities engaged in consultations to identify education and protection priorities.',
  },
];

export interface Publication {
  title: string;
  type: string;
}

export const PUBLICATIONS: Publication[] = [
  {
    title: 'Teachers at the Frontline: Rapid Assessment of Education Needs in Sagaing Region',
    type: 'Rapid Assessment',
  },
  { title: 'Earthquake Impact Assessment', type: 'Assessment' },
  { title: 'Community Education Needs Assessment', type: 'Assessment' },
  { title: 'Learning Briefs', type: 'Learning' },
];
